import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Row,
  Col,
  Tabs,
  Tab,
} from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import API_BASE_URL from "../../config";

export default function DiscountsPage() {
  const [activeTab, setActiveTab] = useState("discounts");

  // Shared
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const stored = localStorage.getItem("selectedCompany");
    return stored ? JSON.parse(stored).id : "";
  });

  // Discounts
  const [discounts, setDiscounts] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    type: "cart_value",
    code: "",
    min_cart_value: "",
    discount_value: "",
    discount_type: "percent",
    branch_id: "",
  });

  // Offers
  const [offers, setOffers] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Track add vs edit
  const [currentOfferId, setCurrentOfferId] = useState(null);

  const [offerForm, setOfferForm] = useState({
    branch_id: "",
    start_date: "",
    end_date: "",
    description: "",
    youtube_video: "",
  });
  const [offerImage, setOfferImage] = useState(null); // New file (for upload)
  const [existingImageUrl, setExistingImageUrl] = useState(""); // Preview current image

  const [offersLoading, setOffersLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Search filters
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const [searchBranch, setSearchBranch] = useState("");

  // Load companies
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanies(res.data || []);

        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsed = JSON.parse(storedCompany);
          const valid = res.data.some((c) => c.id === parsed.id);
          if (!valid) {
            localStorage.removeItem("selectedCompany");
            localStorage.removeItem("selectedBranch");
            setSelectedCompany("");
          } else {
            setSelectedCompany(parsed.id);
          }
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load companies", "error");
      }
    }
    fetchCompanies();
  }, []);

  // Load branches + data when company changes
  useEffect(() => {
    if (!selectedCompany) {
      setBranches([]);
      setDiscounts([]);
      setOffers([]);
      // Reset search filters when company changes
      setSearchStartDate("");
      setSearchEndDate("");
      setSearchBranch("");
      return;
    }

    async function fetchBranches() {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/companies/${selectedCompany}/branches`
        );
        setBranches(res.data || []);
      } catch (err) {
        Swal.fire("Error", "Failed to load branches", "error");
      }
    }

    fetchBranches();
    fetchDiscounts(selectedCompany);
    fetchOffers(selectedCompany);
  }, [selectedCompany]);

  const fetchDiscounts = async (companyId) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/discounts`
      );
      setDiscounts(res.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch discounts", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchOffers = async (companyId) => {
    setOffersLoading(true);
    try {
      const params = {};
      if (searchStartDate) params.start_date = searchStartDate;
      if (searchEndDate) params.end_date = searchEndDate;
      if (searchBranch) params.branch_id = searchBranch;

      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/offers`,
        { params }
      );
      setOffers(res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch offers", "error");
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchOffers(selectedCompany);
    }
  }, [searchStartDate, searchEndDate, searchBranch, selectedCompany]);

  const handleCompanyChange = (e) => {
    const id = e.target.value;
    setSelectedCompany(id);
    const companyObj = companies.find((c) => c.id === id);
    localStorage.setItem("selectedCompany", JSON.stringify(companyObj || {}));
    localStorage.removeItem("selectedBranch");
  };

  // === Discount Handlers (unchanged) ===
  const handleDiscountSave = async () => {
    if (!selectedCompany)
      return Swal.fire("Validation", "Select a company first", "warning");

    try {
      const payload = { ...discountForm, company_id: selectedCompany };
      await axios.post(
        `${API_BASE_URL}/companies/${selectedCompany}/discounts`,
        payload
      );
      Swal.fire("Success", "Discount saved successfully", "success");
      setShowDiscountModal(false);
      setDiscountForm({
        type: "cart_value",
        code: "",
        min_cart_value: "",
        discount_value: "",
        discount_type: "percent",
        branch_id: "",
      });
      fetchDiscounts(selectedCompany);
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to save discount",
        "error"
      );
    }
  };

  const handleDiscountDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Discount?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/discounts/${id}`);
      Swal.fire("Deleted", "Discount deleted successfully", "success");
      fetchDiscounts(selectedCompany);
    } catch (err) {
      Swal.fire("Error", "Failed to delete discount", "error");
    }
  };

  // === Offer Handlers ===

  const openAddOfferModal = () => {
    setIsEditMode(false);
    setCurrentOfferId(null);
    setOfferForm({
      branch_id: "",
      start_date: "",
      end_date: "",
      description: "",
      youtube_video: "",
    });
    setOfferImage(null);
    setExistingImageUrl("");
    setShowOfferModal(true);
  };

  const openEditOfferModal = (offer) => {
    setIsEditMode(true);
    setCurrentOfferId(offer.id);
    setOfferForm({
      branch_id: offer.branch_id || "",
      start_date: offer.start_date,
      end_date: offer.end_date,
      description: offer.description,
      youtube_video: offer.youtube_video || "",
    });
    setExistingImageUrl(offer.image_url || "");
    setOfferImage(null); // No new file selected yet
    setShowOfferModal(true);
  };

  const handleOfferSave = async () => {
    if (!selectedCompany)
      return Swal.fire("Validation", "Select a company first", "warning");

    try {
      const formData = new FormData();
      formData.append("branch_id", offerForm.branch_id || "");
      formData.append("start_date", offerForm.start_date);
      formData.append("end_date", offerForm.end_date);
      formData.append("description", offerForm.description);
      if (offerForm.youtube_video)
        formData.append("youtube_video", offerForm.youtube_video);
      if (offerImage) formData.append("image", offerImage);

      const url = isEditMode
        ? `${API_BASE_URL}/companies/${selectedCompany}/offers/${currentOfferId}`
        : `${API_BASE_URL}/companies/${selectedCompany}/offers`;

      const method = isEditMode ? axios.post : axios.post;

      await method(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire(
        "Success",
        `Offer ${isEditMode ? "updated" : "created"} successfully`,
        "success"
      );
      setShowOfferModal(false);
      fetchOffers(selectedCompany);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} offer`,
        "error"
      );
    }
  };

  const handleOfferDelete = async (offerId) => {
    const result = await Swal.fire({
      title: "Delete Offer?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/offers/${offerId}`);
      Swal.fire("Deleted", "Offer deleted successfully", "success");
      fetchOffers(selectedCompany);
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to delete offer",
        "error"
      );
    }
  };

  return (
    <div className="container mt-4">
      <h3>
        <i className="bi bi-tag me-2"></i>Manage Discounts & Offers
      </h3>

      <Row className="align-items-end mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Company</Form.Label>
            <Form.Select value={selectedCompany} onChange={handleCompanyChange}>
              <option value="">— Select —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        {/* Discounts Tab - unchanged */}
        <Tab eventKey="discounts" title="Discounts">
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="primary"
              onClick={() => setShowDiscountModal(true)}
              disabled={!selectedCompany}
            >
              <i className="bi bi-plus-circle me-2"></i>Add Discount
            </Button>
          </div>

          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Code</th>
                  <th>Min Cart Value</th>
                  <th>Discount</th>
                  <th>Branch</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No discounts found
                    </td>
                  </tr>
                ) : (
                  discounts.map((d) => (
                    <tr key={d.id}>
                      <td>{d.type.replace("_", " ")}</td>
                      <td>{d.code || "-"}</td>
                      <td>₹{d.min_cart_value || 0}</td>
                      <td>
                        {d.discount_type === "percent"
                          ? `${d.discount_value}%`
                          : `₹${d.discount_value}`}
                      </td>
                      <td>{d.branch_name || "All"}</td>
                      <td>
                        <Button
                          variant="link"
                          className="text-danger p-1"
                          onClick={() => handleDiscountDelete(d.id)}
                          title="Delete Discount"
                        >
                          <i className="bi bi-trash fs-5"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Tab>

        {/* Offers Tab */}
        <Tab eventKey="offers" title="Offers">
          <Row className="align-items-end mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={searchStartDate}
                  onChange={(e) => setSearchStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={searchEndDate}
                  onChange={(e) => setSearchEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Select
                  value={searchBranch}
                  onChange={(e) => setSearchBranch(e.target.value)}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="text-end">
              <Button
                variant="primary"
                onClick={openAddOfferModal}
                disabled={!selectedCompany}
              >
                <i className="bi bi-plus-circle me-2"></i>Add Offer
              </Button>
            </Col>
          </Row>

          {offersLoading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Description</th>
                  <th>Image</th>
                  <th>YouTube</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {offers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No offers found
                    </td>
                  </tr>
                ) : (
                  offers.map((o) => (
                    <tr key={o.id}>
                      <td>{o.branch_name || "All"}</td>
                      <td>{o.start_date}</td>
                      <td>{o.end_date}</td>
                      <td>{o.description}</td>
                      <td>
                        {o.image_url ? (
                          <a
                            href={o.image_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View Image
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {o.youtube_video ? (
                          <a
                            href={o.youtube_video}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Watch Video
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className="text-warning p-1 me-2"
                          onClick={() => openEditOfferModal(o)}
                          title="Edit Offer"
                        >
                          <i className="bi bi-pencil fs-5"></i>
                        </Button>
                        <Button
                          variant="link"
                          className="text-danger p-1"
                          onClick={() => handleOfferDelete(o.id)}
                          title="Delete Offer"
                        >
                          <i className="bi bi-trash fs-5"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Tab>
      </Tabs>

      {/* Offer Modal - Add & Edit */}
      <Modal
        show={showOfferModal}
        onHide={() => setShowOfferModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? "Edit Offer" : "Add Offer"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Branch (Optional)</Form.Label>
                  <Form.Select
                    value={offerForm.branch_id}
                    onChange={(e) =>
                      setOfferForm({ ...offerForm, branch_id: e.target.value })
                    }
                  >
                    <option value="">All Branches</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Offer Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={offerForm.start_date}
                    onChange={(e) =>
                      setOfferForm({ ...offerForm, start_date: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Offer End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={offerForm.end_date}
                    onChange={(e) =>
                      setOfferForm({ ...offerForm, end_date: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Offer Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={offerForm.description}
                onChange={(e) =>
                  setOfferForm({ ...offerForm, description: e.target.value })
                }
                required
              />
            </Form.Group>

            {/* Show current image if editing */}
            {isEditMode && existingImageUrl && (
              <Form.Group className="mb-3">
                <Form.Label>Current Image</Form.Label>
                <div>
                  <img
                    src={existingImageUrl}
                    alt="Current offer"
                    style={{ maxHeight: "200px", borderRadius: "8px" }}
                  />
                </div>
                <small className="text-muted">
                  Upload a new image to replace it.
                </small>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Offer Image {isEditMode ? "(Optional - replace)" : "(Optional)"}
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setOfferImage(e.target.files[0] || null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>YouTube Video URL (Optional)</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={offerForm.youtube_video}
                onChange={(e) =>
                  setOfferForm({ ...offerForm, youtube_video: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOfferModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleOfferSave}>
            {isEditMode ? "Update Offer" : "Save Offer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Discount Modal remains unchanged - omitted for brevity but keep it as before */}
      {/* ... your existing Discount Modal ... */}
    </div>
  );
}
